import { useFlags } from "launchdarkly-react-client-sdk";
import { format } from "date-fns/format";
import stableStringify from "fast-json-stable-stringify";
import { capitalize } from "lodash";
import { isPresent } from "ts-extras";

import {
  RelatedColumn,
  StaticValueColumn,
  TraitColumn,
  TraitType,
  TransformedColumn,
  VisualQueryFilter,
} from "@hightouch/lib/query/visual/types";
import { SchemaModelType } from "src/types/schema";
import {
  AndCondition,
  AndOrCondition,
  Condition,
  ConditionType,
  isPropertyCondition,
  ModelSemanticType,
  OrCondition,
  visitConditionList,
} from "src/types/visual";
import { accurateCommaNumber } from "src/utils/numbers";

import { PreviewableColumn } from "./types";
import { produce } from "immer";

export enum RefreshIntervals {
  Hourly = 3600,
  Daily = 86400,
  Weekly = 604800,
  BiWeekly = 1209600,
  Monthly = 2419200,
  ManualOnly = -1,
}

export const REFRESH_OPTIONS = [
  {
    label: "Hourly",
    value: RefreshIntervals.Hourly,
  },
  {
    label: "Daily",
    value: RefreshIntervals.Daily,
  },
  {
    label: "Weekly",
    value: RefreshIntervals.Weekly,
  },
  {
    label: "Bi-weekly",
    value: RefreshIntervals.BiWeekly,
  },
  {
    label: "Monthly",
    value: RefreshIntervals.Monthly,
  },
  {
    label: "Manual Only",
    value: RefreshIntervals.ManualOnly,
  },
];

export function toSingleCondition(
  conditions: AndCondition[] | OrCondition[] | Condition[] | undefined,
): AndOrCondition<AndCondition | OrCondition>[] | undefined {
  if (!conditions) {
    return undefined;
  }

  // Tech debt: conditions may only have one condition at the top level.
  // See https://carryinternal.slack.com/archives/C024T56QQLW/p1669910261515869
  if (Array.isArray(conditions)) {
    if (conditions.length === 0) {
      return [];
    }

    if (conditions.length > 1) {
      return [{ type: ConditionType.And, conditions }];
    }

    /* Start: new format. One condition at the top level */

    const condition = conditions?.[0];
    const hasTopLevelAndOrCondition =
      condition?.type === ConditionType.And ||
      condition?.type === ConditionType.Or;

    // Verify top level condition is an and/or
    // If not, wrap it with `AND` condition
    if (
      // Has not been formatted yet
      !hasTopLevelAndOrCondition
    ) {
      return [{ type: ConditionType.And, conditions }];
    }

    // There is only one AND or OR condition that has been formatted correctly.
    // Its immediate children (if there are any) are OR conditions
    return conditions as AndCondition<AndCondition>[];

    /* End: new format */
  }

  return [conditions];
}

const schemaModelTypePaths: Record<SchemaModelType, string> = {
  [SchemaModelType.Parent]: "parent-models",
  [SchemaModelType.Related]: "related-models",
  [SchemaModelType.Event]: "events",
  [SchemaModelType.RealtimeEvent]: "realtime-events",
  [SchemaModelType.Interaction]: "events",
  [SchemaModelType.Catalog]: "catalogs",
  [SchemaModelType.Group]: "group",
  [SchemaModelType.Asset]: "assets",
  [SchemaModelType.AdStats]: "ad-stats",
  [SchemaModelType.MessageEvent]: "message-events",
  [SchemaModelType.General]: "general-models",
};

export const getSchemaModelTypeFromPath = (path: string) => {
  const type = Object.entries(schemaModelTypePaths).find(
    ([, p]) => p === path,
  )?.[0];
  return type as SchemaModelType;
};

export const getSchemaModelType = (object: {
  type: string | null;
  is_general_model?: boolean | null;
  event: {
    interaction: any;
    realtime_event_source_id: string | null;
  } | null;
  asset?: {
    type: string;
  } | null;
  ad_stats?: {
    __typename: string;
  } | null;
  primary_key: string | null;
}) => {
  if (object.is_general_model) {
    return SchemaModelType.General;
  }
  if (object.type === "catalog") {
    return SchemaModelType.Catalog;
  }
  if (!object.event && object.primary_key) {
    return SchemaModelType.Parent;
  }
  if (object.ad_stats) {
    return SchemaModelType.AdStats;
  }
  if (object.event) {
    if (
      object.type === SchemaModelType.Interaction &&
      object.event.interaction
    ) {
      return SchemaModelType.Interaction;
    }

    if (object.event.realtime_event_source_id) {
      return SchemaModelType.RealtimeEvent;
    }

    return SchemaModelType.Event;
  }

  if (object.asset && object.type === SchemaModelType.Asset) {
    return SchemaModelType.Asset;
  }
  return SchemaModelType.Related;
};

export const getTypeName = (type: SchemaModelType) => {
  switch (type) {
    case SchemaModelType.Event:
    case SchemaModelType.Interaction:
      return "event";
    case SchemaModelType.Catalog:
      return "catalog";
    case SchemaModelType.RealtimeEvent:
      return "realtime event";
    case SchemaModelType.MessageEvent:
      return "message event";
    default:
      return `${type} model`;
  }
};

export const getSchemaModelTypeNames = (type: SchemaModelType) => {
  const typeName = getTypeName(type);
  const typePath = schemaModelTypePaths[type];
  const typeNameCapitalized = capitalize(typeName);

  return {
    typePath,
    typeName,
    typeNameCapitalized,
  };
};

export const SUPPORTED_JSON_ARRAY_SOURCES = [
  "postgres",
  "databricks",
  "bigquery",
  "snowflake",
  "trino",
];

export const SUPPORTED_BIGINT_SOURCES = ["databricks", "bigquery"];

export const useBigintSupport = (source: string) => {
  const { useNewBigintType } = useFlags();

  return {
    allowBigints: useNewBigintType && SUPPORTED_BIGINT_SOURCES.includes(source),
  };
};

// Returns a filterable column from a trait definition, but without the fields used to define which model it belongs to
export const traitDefinitionToPreviewableColumn = (trait: {
  id: string;
  type: string;
  name: string;
  relationship: {
    id: string;
    name: string | null;
  } | null;
}): PreviewableColumn => {
  const isFormulaTrait = trait.type === TraitType.Formula;

  const column: TraitColumn = {
    type: "trait",
    traitDefinitionId: trait.id,
    conditions: [],
    name: trait.name,
  };

  return {
    alias: null,
    name: trait.name,
    model_name: trait.relationship?.name ?? "",
    column_reference: isFormulaTrait
      ? {
          type: "transformed",
          column,
        }
      : {
          type: "related",
          column,
          // Should always be present, but filter to appease the compiler gods
          // An empty path would cause a non-formula trait to fail
          path: [trait.relationship?.id].filter(isPresent),
        },
  };
};

export const traitEnrichmentToPreviewableColumn = ({
  alias,
  column,
}: {
  alias: string;
  column: RelatedColumn | TransformedColumn | StaticValueColumn;
}): PreviewableColumn => ({
  alias: alias,
  name: alias,
  column_reference: column,
  // This is only displayed to differentiate merged columns, so we can effectively ignore it
  model_name: "Trait enrichment",
});

export const limitErrorMessage = (error: string) => {
  const count = parseInt(error.split("storing only ")[1] || "10000");
  return `Too many distinct values. Only the ${accurateCommaNumber(
    count,
  )} most frequent values will be shown as suggestions.`;
};

export const checkIsLimitError = (error?: string | null) =>
  error?.startsWith("number of distinct values exceeds limit") ?? false;

export const getMostRecentAudienceSizeSource = ({
  lastPreviewedDate,
  lastCachedDate,
}: {
  lastPreviewedDate?: string | null;
  lastCachedDate: string | null;
}): "cache" | "preview" | null => {
  // Preview is always the most recent and therefore considered the source-of-truth
  if (lastPreviewedDate) return "preview";
  if (lastCachedDate) return "cache";
  return null;
};

export const formatDate = (date: string | number | Date) =>
  format(date, "MMM d · p");

/**
 * Checks if the visual query has event conditions that reference realtime event models.
 *
 * @param condition - The condition to check.
 * @param realtimeEventModelIds - The set of realtime event model IDs.
 */
export const audienceHasRealtimeEvents = (
  conditions: Condition[] | undefined | null,
  realtimeEventModelIds: Set<string>,
): boolean => {
  if (!conditions) {
    return false;
  }

  const visualQueryEventModelIds: string[] = [];

  visitConditionList(conditions, {
    [ConditionType.Event]: (condition) => {
      if (condition.eventModelId) {
        visualQueryEventModelIds.push(condition.eventModelId.toString());
      }
    },
  });

  return visualQueryEventModelIds.some((id) => realtimeEventModelIds.has(id));
};

/**
 * Returns true when the audience has at least one condition that has to be
 * evaluated against the warehouse (i.e. requires a realtime audience cache /
 * warehouse pull). Realtime-event conditions whose event model is in
 * `realtimeEventModelIds` are excluded — they are evaluated by the event
 * pipeline and never participate in the warehouse pull.
 */
export const audienceHasWarehouseConditions = (
  conditions: Condition[] | undefined | null,
  realtimeEventModelIds: Set<string>,
): boolean => {
  if (!conditions) {
    return false;
  }

  let hasWarehouseLeaf = false;

  visitConditionList(
    conditions,
    {
      [ConditionType.Property]: () => {
        hasWarehouseLeaf = true;
      },
      [ConditionType.NumberOf]: () => {
        hasWarehouseLeaf = true;
      },
      [ConditionType.Event]: (condition) => {
        const eventModelId = condition.eventModelId?.toString();
        if (!eventModelId || !realtimeEventModelIds.has(eventModelId)) {
          hasWarehouseLeaf = true;
        }
      },
    },
    { visitSubconditions: false },
  );

  return hasWarehouseLeaf;
};

export type StaleWarehouseConditionKind = "added" | "modified";

/**
 * Returns the warehouse-relevant leaf conditions that have been added or
 * modified since `snapshotConditions` (the audience as it looked at the
 * last successful warehouse pull), keyed by id and tagged with the kind
 * of change. Realtime-event-only conditions are ignored because they
 * don't require a warehouse pull to take effect.
 *
 * Leaves are matched by their `id`. A leaf in the current tree whose id
 * doesn't exist in the snapshot is tagged "added". A leaf whose id is in
 * the snapshot but whose serialized shape (property, operator, value,
 * etc.) differs is tagged "modified". Re-ordering within an AND/OR group
 * is not flagged on its own.
 */
export const getStaleWarehouseConditions = (
  snapshotConditions: Condition[] | undefined | null,
  currentConditions: Condition[] | undefined | null,
  realtimeEventModelIds: Set<string>,
): Map<string, StaleWarehouseConditionKind> => {
  const stale = new Map<string, StaleWarehouseConditionKind>();
  if (!currentConditions?.length) {
    return stale;
  }

  const snapshotByLeafId = new Map<string, string>();
  if (snapshotConditions?.length) {
    const indexLeaf = (condition: Condition) => {
      if (condition.id) {
        snapshotByLeafId.set(condition.id, serializeLeaf(condition));
      }
    };
    visitConditionList(
      snapshotConditions,
      {
        [ConditionType.Property]: indexLeaf,
        [ConditionType.NumberOf]: indexLeaf,
        [ConditionType.Event]: indexLeaf,
      },
      { visitSubconditions: false },
    );
  }

  const markIfChanged = (condition: Condition) => {
    if (!condition.id) {
      return;
    }
    const snapshotShape = snapshotByLeafId.get(condition.id);
    if (snapshotShape === undefined) {
      stale.set(condition.id, "added");
      return;
    }
    if (snapshotShape !== serializeLeaf(condition)) {
      stale.set(condition.id, "modified");
    }
  };

  visitConditionList(
    currentConditions,
    {
      [ConditionType.Property]: markIfChanged,
      [ConditionType.NumberOf]: markIfChanged,
      [ConditionType.Event]: (condition) => {
        const eventModelId = condition.eventModelId?.toString();
        if (!eventModelId || !realtimeEventModelIds.has(eventModelId)) {
          markIfChanged(condition);
        }
      },
    },
    { visitSubconditions: false },
  );

  return stale;
};

const serializeLeaf = (condition: Condition): string => {
  const { id: _id, ...rest } = condition as Condition & { id?: string };
  return stableStringify(rest);
};

export function hydrateConditionWithIds<
  T extends Condition | VisualQueryFilter | null | undefined,
>(condition: T, overwriteExistingIds = false): T {
  if (typeof condition === "undefined") return condition;
  return produce(condition, (draft) =>
    hydrateConditionWithIdsMutative(draft, overwriteExistingIds),
  );
}

export const hydrateConditionWithIdsMutative = <
  T extends Condition | VisualQueryFilter,
>(
  condition: T,
  overwriteExistingIds = false,
  createId: () => string = () => crypto.randomUUID(),
): void => {
  if (typeof condition !== "object" || condition == null) {
    return;
  }
  if (overwriteExistingIds || !("id" in condition) || condition.id == null) {
    condition.id = createId();
  }
  if ("conditions" in condition && Array.isArray(condition.conditions)) {
    condition.conditions.forEach((c) =>
      hydrateConditionWithIdsMutative(c, overwriteExistingIds, createId),
    );
  }
  if ("subconditions" in condition && Array.isArray(condition.subconditions)) {
    condition.subconditions.forEach((c) =>
      hydrateConditionWithIdsMutative(c, overwriteExistingIds, createId),
    );
  }
  if (
    isPropertyCondition(condition) &&
    "property" in condition &&
    condition.property != null &&
    typeof condition.property === "object" &&
    "column" in condition.property &&
    condition.property.column != null &&
    typeof condition.property.column === "object" &&
    "conditions" in condition.property.column &&
    Array.isArray(condition.property.column.conditions)
  ) {
    condition.property.column.conditions.forEach((c) => {
      hydrateConditionWithIdsMutative(c, overwriteExistingIds, createId);
    });
  }
};

export const getSemanticTypeTooltip = (
  semanticType: ModelSemanticType | null,
) => {
  if (!semanticType) {
    return undefined;
  }

  return `The rows in this model represent ${semanticType}`;
};
